import React, { useCallback, useState } from 'react';
import { ChevronDown, CircleCheck, CircleX, Download, LucideIcon, Paperclip, Upload } from 'lucide-react';
import { ImportResult } from '../../services/adminService';
import { Button, Checkbox, EntityIcon, SegmentedControl, Spinner } from '../ui';
import type { EntityKind, SegmentedItem } from '../ui';

/*
 * Presentational pieces shared by the Data page rows (stakeholders, organizations, bulk).
 * State and API calls stay in the owning components.
 */

export type TransferFormat = 'csv' | 'excel';

export const FORMAT_ITEMS: SegmentedItem<TransferFormat>[] = [
  { value: 'excel', label: 'Excel' },
  { value: 'csv', label: 'CSV' },
];

/* ---------- last activity (per browser) ---------- */

interface TransferRecord {
  at: string;
  count?: number;
}

const STORAGE_PREFIX = 'staketory.data.';

const readRecord = (key: string): TransferRecord | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? (JSON.parse(raw) as TransferRecord) : null;
  } catch {
    return null;
  }
};

const writeRecord = (key: string, record: TransferRecord) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(record));
  } catch {
    // Storage can be unavailable (private mode); the meta line just stays empty
  }
};

export const timeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Date.now() - then) / 1000;
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** Remembers when an entity was last exported / imported in this browser, for the row meta line. */
export const useTransferLog = (entity: string) => {
  const [lastExport, setLastExport] = useState<TransferRecord | null>(() => readRecord(`${entity}.export`));
  const [lastImport, setLastImport] = useState<TransferRecord | null>(() => readRecord(`${entity}.import`));

  const markExport = useCallback(() => {
    const record = { at: new Date().toISOString() };
    writeRecord(`${entity}.export`, record);
    setLastExport(record);
  }, [entity]);

  const markImport = useCallback(
    (count: number) => {
      const record = { at: new Date().toISOString(), count };
      writeRecord(`${entity}.import`, record);
      setLastImport(record);
    },
    [entity],
  );

  return { lastExport, lastImport, markExport, markImport };
};

export const transferMeta = (lastExport: TransferRecord | null, lastImport: TransferRecord | null) => {
  const parts: string[] = [];
  if (lastExport) parts.push(`Exported ${timeAgo(lastExport.at)}`);
  if (lastImport) parts.push(`Imported ${lastImport.count ?? 0} · ${timeAgo(lastImport.at)}`);
  return parts.length ? parts.join(' · ') : 'Excel · CSV';
};

/* ---------- layout pieces ---------- */

type Panel = 'import' | 'export' | null;

export const RowShell: React.FC<{
  icon: LucideIcon;
  /** Tints the icon with the entity hue (stakeholders sky, organizations teal) */
  kind?: EntityKind;
  label: string;
  meta: React.ReactNode;
  actions: React.ReactNode;
  expanded: boolean;
  children?: React.ReactNode;
}> = ({ icon: Icon, kind, label, meta, actions, expanded, children }) => (
  <div className={`transition-colors ${expanded ? 'bg-neutral-50/60' : ''}`}>
    <div className="flex items-center gap-3 px-4 py-2.5">
      {kind ? (
        <EntityIcon kind={kind} icon={Icon} size="md" className="hidden sm:inline-flex" />
      ) : (
        <span className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-neutral-100 text-neutral-500 sm:inline-flex">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] text-ink">{label}</p>
        <p className="truncate text-[12px] text-neutral-400">{meta}</p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1.5">{actions}</div>
    </div>
    {children}
  </div>
);

/** Secondary pill button that toggles an inline panel */
export const PanelToggle: React.FC<{
  icon: LucideIcon;
  label: string;
  open: boolean;
  onClick: () => void;
  controls: string;
}> = ({ icon: Icon, label, open, onClick, controls }) => (
  <Button
    size="sm"
    variant="secondary"
    onClick={onClick}
    aria-expanded={open}
    aria-controls={controls}
    className={`px-3 ${open ? '!bg-neutral-100' : ''}`}
  >
    <Icon className="hidden h-4 w-4 text-neutral-400 sm:block" strokeWidth={1.75} />
    {label}
    <ChevronDown
      className={`hidden h-3.5 w-3.5 text-neutral-400 transition-transform duration-200 sm:block ${open ? 'rotate-180' : ''}`}
      strokeWidth={1.75}
    />
  </Button>
);

export const PanelBody: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <div id={id} className="animate-fade-up space-y-3 px-4 pb-4 pt-1 sm:pl-[60px]">
    {children}
  </div>
);

export const PanelLine: React.FC<{ label: string; title?: string; children: React.ReactNode }> = ({
  label,
  title,
  children,
}) => (
  <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[72px_minmax(0,1fr)] sm:items-center">
    <span className="text-[12px] text-neutral-500" title={title}>
      {label}
    </span>
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">{children}</div>
  </div>
);

/** Indeterminate thin progress bar */
export const ProgressLine: React.FC<{ label: React.ReactNode }> = ({ label }) => (
  <div role="status" className="px-4 pb-3 sm:pl-[60px]">
    <div className="flex items-center justify-between gap-3 text-[12px] text-neutral-500">
      <span className="truncate">{label}</span>
      <Spinner className="h-3 w-3 text-neutral-400" />
    </div>
    <style>{'@keyframes data-progress{0%{transform:translateX(-100%)}100%{transform:translateX(320%)}}'}</style>
    <div className="mt-1.5 h-0.5 overflow-hidden rounded-full bg-neutral-100">
      <div className="h-full w-1/3 rounded-full bg-ink" style={{ animation: 'data-progress 1.2s ease-in-out infinite' }} />
    </div>
  </div>
);

export const InlineError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-4 pb-3 sm:pl-[60px]">
    <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
      {children}
    </p>
  </div>
);

const ImportResultLine: React.FC<{ result: ImportResult; noun: string }> = ({ result, noun }) => {
  const [open, setOpen] = useState(!result.success);
  const issues = result.errors.length + result.warnings.length;
  return (
    <div role="status" className="animate-fade-up px-4 pb-3 sm:pl-[60px]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        {result.success ? (
          <CircleCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} />
        ) : (
          <CircleX className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
        )}
        <span className="text-ink">
          {result.success ? 'Imported' : 'Import failed'}
          <span className="text-neutral-500">
            {' · '}
            <span className={`tabular-nums ${result.success && result.imported > 0 ? 'text-emerald-700' : ''}`}>
              {result.imported}
            </span>{' '}
            {noun}
          </span>
        </span>
        {result.errors.length > 0 && (
          <span className="tabular-nums text-primary-700">
            · {result.errors.length} {result.errors.length === 1 ? 'error' : 'errors'}
          </span>
        )}
        {result.warnings.length > 0 && (
          <span className="tabular-nums text-amber-700">
            · {result.warnings.length} {result.warnings.length === 1 ? 'warning' : 'warnings'}
          </span>
        )}
        {issues > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="ml-auto inline-flex items-center gap-0.5 rounded-full text-[12px] text-neutral-500 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
          >
            Details
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
          </button>
        )}
      </div>
      {open && issues > 0 && (
        <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto pl-6 text-[12px]">
          {result.errors.map((message, index) => (
            <li key={`e-${index}`} className="break-words text-primary-700">
              {message}
            </li>
          ))}
          {result.warnings.map((message, index) => (
            <li key={`w-${index}`} className="break-words text-amber-700">
              {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ---------- entity row ---------- */

/** The values the API stores; the labels are what a person reads. */
export const EXPORT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export type IncludeKey = 'assignments' | 'influence' | 'wiki' | 'media';

const INCLUDE_OPTIONS: { key: IncludeKey; label: string; title: string }[] = [
  { key: 'assignments', label: 'Assignments', title: 'Roles, departments, dates' },
  { key: 'influence', label: 'Influence', title: 'Influence scores & categories' },
  { key: 'wiki', label: 'Wiki', title: 'Wiki pages & documentation' },
  { key: 'media', label: 'Media', title: 'Media assets & files' },
];

export interface ImportExportRowProps {
  id: string;
  icon: LucideIcon;
  /** Entity hue for the row icon */
  kind?: EntityKind;
  label: string;
  /** Plural noun used in results, e.g. "stakeholders" */
  noun: string;
  meta: React.ReactNode;

  fileInputRef: React.RefObject<HTMLInputElement | null>;
  importFile: File | null;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  importFormat: TransferFormat;
  onImportFormatChange: (format: TransferFormat) => void;
  importing: boolean;
  onImport: () => void;
  onDownloadTemplate: (format: TransferFormat) => void;
  importResult: ImportResult | null;

  exportFormat: TransferFormat;
  onExportFormatChange: (format: TransferFormat) => void;
  exportStatus: string[];
  onStatusChange: (status: string, checked: boolean) => void;
  include: Record<IncludeKey, boolean>;
  onIncludeChange: (key: IncludeKey, checked: boolean) => void;
  exporting: boolean;
  onExport: () => void;

  error: string | null;
}

export const ImportExportRow: React.FC<ImportExportRowProps> = (props) => {
  const [panel, setPanel] = useState<Panel>(null);
  const toggle = (next: Exclude<Panel, null>) => setPanel((current) => (current === next ? null : next));
  const importId = `${props.id}-import`;
  const exportId = `${props.id}-export`;

  return (
    <RowShell
      icon={props.icon}
      kind={props.kind}
      label={props.label}
      meta={props.meta}
      expanded={panel !== null}
      actions={
        <>
          <PanelToggle icon={Upload} label="Import" open={panel === 'import'} onClick={() => toggle('import')} controls={importId} />
          <PanelToggle icon={Download} label="Export" open={panel === 'export'} onClick={() => toggle('export')} controls={exportId} />
        </>
      }
    >
      {panel === 'import' && (
        <PanelBody id={importId}>
          <PanelLine label="File">
            <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full bg-white px-3 text-[13px] font-medium text-ink ring-1 ring-inset ring-neutral-200 transition-colors focus-within:ring-2 focus-within:ring-primary-600/40 hover:bg-neutral-50">
              <Paperclip className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
              {props.importFile ? 'Change' : 'Choose file'}
              <input
                ref={props.fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={props.onFileSelect}
                className="sr-only"
              />
            </label>
            {props.importFile && (
              <span className="min-w-0 truncate font-ui-mono text-[12px] text-neutral-500" title={props.importFile.name}>
                {props.importFile.name}
              </span>
            )}
          </PanelLine>
          <PanelLine label="Format">
            <SegmentedControl label="Import format" items={FORMAT_ITEMS} value={props.importFormat} onChange={props.onImportFormatChange} />
          </PanelLine>
          <PanelLine label="Template">
            <div className="-ml-2.5 flex items-center gap-1">
              <Button size="xs" variant="ghost" onClick={() => props.onDownloadTemplate('excel')}>
                <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                Excel
              </Button>
              <Button size="xs" variant="ghost" onClick={() => props.onDownloadTemplate('csv')}>
                <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                CSV
              </Button>
            </div>
          </PanelLine>
          <div className="flex justify-end">
            <Button size="sm" variant="dark" onClick={props.onImport} disabled={!props.importFile} loading={props.importing}>
              Import
            </Button>
          </div>
        </PanelBody>
      )}

      {panel === 'export' && (
        <PanelBody id={exportId}>
          <PanelLine label="Format">
            <SegmentedControl label="Export format" items={FORMAT_ITEMS} value={props.exportFormat} onChange={props.onExportFormatChange} />
          </PanelLine>
          <PanelLine label="Status" title="Leave empty to export every status">
            {EXPORT_STATUS_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={props.exportStatus.includes(option.value)}
                onChange={(event) => props.onStatusChange(option.value, event.target.checked)}
              />
            ))}
          </PanelLine>
          <PanelLine
            label="Include"
            title="Related data adds UUIDs for a complete restore. Templates show the full column structure."
          >
            {INCLUDE_OPTIONS.map((option) => (
              <Checkbox
                key={option.key}
                label={<span title={option.title}>{option.label}</span>}
                checked={props.include[option.key]}
                onChange={(event) => props.onIncludeChange(option.key, event.target.checked)}
              />
            ))}
          </PanelLine>
          <div className="flex justify-end">
            <Button size="sm" variant="dark" onClick={props.onExport} loading={props.exporting}>
              <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
              Download
            </Button>
          </div>
        </PanelBody>
      )}

      {props.importing && <ProgressLine label={`Importing ${props.importFile?.name ?? props.noun}`} />}
      {props.exporting && <ProgressLine label={`Exporting ${props.noun}`} />}
      {props.error && <InlineError>{props.error}</InlineError>}
      {props.importResult && !props.importing && (
        <ImportResultLine
          key={`${props.importResult.success}-${props.importResult.imported}-${props.importResult.errors.length}-${props.importResult.warnings.length}`}
          result={props.importResult}
          noun={props.noun}
        />
      )}
    </RowShell>
  );
};
